import { computed, Injectable, signal } from '@angular/core';

export type ThemeMode = 'light' | 'dark';
export type ThemeName = 'light' | 'dark' | 'custom';

export interface CustomTheme {
  primary?: string;
  primaryRgb?: string;
  secondary?: string;
  secondaryRgb?: string;
  controlAccent?: string;
  controlAccentRgb?: string;
  info?: string;
  infoRgb?: string;
  success?: string;
  successRgb?: string;
  warning?: string;
  warningRgb?: string;
  danger?: string;
  dangerRgb?: string;
  gradientPrimary?: string;
  gradientAccent?: string;
  gradientWarm?: string;
  gradientCool?: string;
  sidebarBackground?: string;
  headerBackground?: string;
  sidebarColor?: string;
  headerColor?: string;
  sidebarCaptionColor?: string;
  sidebarHoverBackground?: string;
  sidebarHoverColor?: string;
  sidebarActiveBackground?: string;
  sidebarActiveColor?: string;
  headerHoverBackground?: string;
  headerHoverColor?: string;
  bodyHoverBackground?: string;
  bodyHoverColor?: string;
  background?: string;
  backgroundRgb?: string;
  surface?: string;
  surfaceRgb?: string;
  surfaceVariant?: string;
  surfaceVariantRgb?: string;
  text?: string;
  textRgb?: string;
  border?: string;
  borderRgb?: string;
}

export interface ThemeState {
  name: ThemeName;
  mode: ThemeMode;
  custom?: CustomTheme;
  customByMode?: {
    light?: CustomTheme;
    dark?: CustomTheme;
  };
  fontId?: string;
  fontFamily?: string;
  fontSize?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private static readonly storageKey = 'rma.theme';
  private static readonly fontIdKey = 'rma.theme.fontId';
  private static readonly fontFamilyKey = 'rma.theme.fontFamily';
  private static readonly fontSizeKey = 'rma.theme.fontSize';
  private readonly isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';

  private readonly currentTheme = signal<ThemeState>({
    name: 'light',
    mode: 'light',
  });

  readonly theme = computed(() => this.currentTheme());
  readonly darkMode = computed(() => this.currentTheme().mode === 'dark');

  initializeTheme(): void {
    const stored = this.loadTheme();
    let next: ThemeState;
    if (stored) {
      next = stored;
    } else if (this.isBrowser && window.matchMedia?.('(prefers-color-scheme: dark)').matches) {
      next = { name: 'dark', mode: 'dark' };
    } else {
      next = { name: 'light', mode: 'light' };
    }

    if (this.isBrowser) {
      const storage = this.getStorage();
      const fontId = storage?.getItem(ThemeService.fontIdKey) ?? undefined;
      const fontFamily = storage?.getItem(ThemeService.fontFamilyKey) ?? undefined;
      const fontSize = storage?.getItem(ThemeService.fontSizeKey) ?? undefined;
      if (fontId) {
        next = { ...next, fontId };
      }
      if (fontFamily) {
        next = { ...next, fontFamily };
      }
      if (fontSize) {
        next = { ...next, fontSize };
      }
    }

    this.currentTheme.set(next);
    this.applyTheme(next);
  }

  setThemeMode(mode: ThemeMode): void {
    const current = this.currentTheme();
    const customByMode = current.customByMode;
    const custom = current.name === 'custom' && customByMode
      ? (mode === 'dark' ? customByMode.dark ?? current.custom : customByMode.light ?? current.custom)
      : current.custom;
    const next: ThemeState = {
      ...current,
      mode,
      name: current.name === 'custom' ? 'custom' : mode,
      custom,
    };

    this.currentTheme.set(next);
    this.applyTheme(next);
    this.persistTheme();
  }

  toggleDarkMode(): void {
    this.setThemeMode(this.darkMode() ? 'light' : 'dark');
  }

  setCustomTheme(palette: CustomTheme, mode: ThemeMode = this.currentTheme().mode): void {
    const current = this.currentTheme();
    const next: ThemeState = {
      name: 'custom',
      mode,
      custom: { ...palette },
      fontId: current.fontId,
      fontFamily: current.fontFamily,
      fontSize: current.fontSize,
    };

    this.currentTheme.set(next);
    this.applyTheme(next);
    this.persistTheme();
  }

  clearCustomTheme(): void {
    const current = this.currentTheme();
    const mode = current.mode;
    const next: ThemeState = {
      name: mode,
      mode,
      fontId: current.fontId,
      fontFamily: current.fontFamily,
      fontSize: current.fontSize,
    };
    this.currentTheme.set(next);
    this.applyTheme(next);
    this.persistTheme();
  }

  setTheme(theme: ThemeState): void {
    const normalized = this.normalizeTheme(theme);
    this.currentTheme.set(normalized);
    this.applyTheme(normalized);
    this.persistTheme();
  }

  setCustomThemeWithModes(light: CustomTheme, dark: CustomTheme, mode: ThemeMode = this.currentTheme().mode): void {
    const current = this.currentTheme();
    const next: ThemeState = {
      name: 'custom',
      mode,
      custom: { ...(mode === 'dark' ? dark : light) },
      customByMode: {
        light: { ...light },
        dark: { ...dark },
      },
      fontId: current.fontId,
      fontFamily: current.fontFamily,
      fontSize: current.fontSize,
    };

    this.currentTheme.set(next);
    this.applyTheme(next);
    this.persistTheme();
  }

  previewTheme(theme: ThemeState): void {
    const normalized = this.normalizeTheme(theme);
    this.currentTheme.set(normalized);
    this.applyTheme(normalized);
  }

  previewCustomThemeWithModes(light: CustomTheme, dark: CustomTheme, mode: ThemeMode = this.currentTheme().mode): void {
    const current = this.currentTheme();
    const next: ThemeState = {
      name: 'custom',
      mode,
      custom: { ...(mode === 'dark' ? dark : light) },
      customByMode: {
        light: { ...light },
        dark: { ...dark },
      },
      fontId: current.fontId,
      fontFamily: current.fontFamily,
      fontSize: current.fontSize,
    };

    this.currentTheme.set(next);
    this.applyTheme(next);
  }

  getThemeSnapshot(): ThemeState {
    const current = this.currentTheme();
    return {
      ...current,
      custom: current.custom ? { ...current.custom } : undefined,
      customByMode: current.customByMode
        ? {
            light: current.customByMode.light ? { ...current.customByMode.light } : undefined,
            dark: current.customByMode.dark ? { ...current.customByMode.dark } : undefined,
          }
        : undefined,
    };
  }

  private applyTheme(theme: ThemeState): void {
    if (!this.isBrowser) {
      return;
    }

    const root = document.documentElement;
    root.setAttribute('data-bs-theme', theme.mode);
    this.setVar(root, '--theme-font-family', theme.fontFamily);
    this.setVar(root, '--bs-body-font-family', theme.fontFamily);
    this.setVar(root, '--theme-font-size-base', theme.fontSize);
    this.setVar(root, '--bs-body-font-size', theme.fontSize);

    if (theme.name === 'custom') {
      root.setAttribute('data-theme', 'custom');
      this.applyCustomTheme(theme.custom);
    } else {
      root.removeAttribute('data-theme');
      this.clearCustomThemeVars();
    }

    this.applySelectIcons(theme);
  }

  private applySelectIcons(theme: ThemeState): void {
    if (!this.isBrowser) {
      return;
    }

    const root = document.documentElement;
    const css = getComputedStyle(root);
    const fallbackEmpty = theme.mode === 'dark' ? '#94a3b8' : '#6b7280';
    const fallbackFilled = theme.mode === 'dark' ? '#70b4ff' : '#3b82f6';

    const emptyColor = this.readCssColor(css, '--theme-select-placeholder-color')
      ?? this.readCssColor(css, '--bs-secondary-color')
      ?? fallbackEmpty;
    const filledColor = this.readCssColor(css, '--theme-control-accent')
      ?? this.readCssColor(css, '--bs-primary')
      ?? fallbackFilled;

    this.setVar(root, '--theme-select-icon-empty', this.buildSelectIconDataUri(emptyColor, 'empty'));
    this.setVar(root, '--theme-select-icon-filled', this.buildSelectIconDataUri(filledColor, 'filled'));
  }

  private readCssColor(css: CSSStyleDeclaration, name: string): string | undefined {
    const value = css.getPropertyValue(name).trim();
    return value.length > 0 ? value : undefined;
  }

  private buildSelectIconDataUri(color: string, variant: 'empty' | 'filled'): string {
    const svg = variant === 'filled'
      ? `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="${color}"><path d="M7 10h10l-5 6z"/></svg>`
      : `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 10h10l-5 6z"/></svg>`;

    return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
  }

  private applyCustomTheme(custom?: CustomTheme): void {
    if (!this.isBrowser) {
      return;
    }

    const root = document.documentElement;
    const palette = custom ?? {};

    this.setVar(root, '--custom-primary', palette.primary);
    this.setVar(root, '--custom-primary-rgb', this.toRgb(palette.primary) ?? palette.primaryRgb);
    this.setVar(root, '--custom-secondary', palette.secondary);
    this.setVar(root, '--custom-secondary-rgb', this.toRgb(palette.secondary) ?? palette.secondaryRgb);
    this.setVar(root, '--custom-control-accent', palette.controlAccent);
    this.setVar(root, '--custom-control-accent-rgb', this.toRgb(palette.controlAccent) ?? palette.controlAccentRgb);
    this.setVar(root, '--custom-info', palette.info);
    this.setVar(root, '--custom-info-rgb', this.toRgb(palette.info) ?? palette.infoRgb);
    this.setVar(root, '--custom-success', palette.success);
    this.setVar(root, '--custom-success-rgb', this.toRgb(palette.success) ?? palette.successRgb);
    this.setVar(root, '--custom-warning', palette.warning);
    this.setVar(root, '--custom-warning-rgb', this.toRgb(palette.warning) ?? palette.warningRgb);
    this.setVar(root, '--custom-danger', palette.danger);
    this.setVar(root, '--custom-danger-rgb', this.toRgb(palette.danger) ?? palette.dangerRgb);
    this.setVar(root, '--custom-gradient-primary', palette.gradientPrimary);
    this.setVar(root, '--custom-gradient-accent', palette.gradientAccent);
    this.setVar(root, '--custom-gradient-warm', palette.gradientWarm);
    this.setVar(root, '--custom-gradient-cool', palette.gradientCool);
    this.setVar(root, '--custom-sidebar-background', palette.sidebarBackground);
    this.setVar(root, '--custom-header-background', palette.headerBackground);
    this.setVar(root, '--custom-sidebar-color', palette.sidebarColor);
    this.setVar(root, '--custom-header-color', palette.headerColor);
    this.setVar(root, '--custom-sidebar-caption-color', palette.sidebarCaptionColor);
    this.setVar(root, '--custom-sidebar-hover-background', palette.sidebarHoverBackground);
    this.setVar(root, '--custom-sidebar-hover-color', palette.sidebarHoverColor);
    this.setVar(root, '--custom-sidebar-active-background', palette.sidebarActiveBackground);
    this.setVar(root, '--custom-sidebar-active-color', palette.sidebarActiveColor);
    this.setVar(root, '--custom-header-hover-background', palette.headerHoverBackground);
    this.setVar(root, '--custom-header-hover-color', palette.headerHoverColor);
    this.setVar(root, '--custom-body-hover-background', palette.bodyHoverBackground);
    this.setVar(root, '--custom-body-hover-color', palette.bodyHoverColor);

    this.setVar(root, '--custom-background', palette.background);
    this.setVar(root, '--custom-background-rgb', this.toRgb(palette.background) ?? palette.backgroundRgb);
    this.setVar(root, '--custom-surface', palette.surface);
    this.setVar(root, '--custom-surface-rgb', this.toRgb(palette.surface) ?? palette.surfaceRgb);
    this.setVar(root, '--custom-surface-variant', palette.surfaceVariant);
    this.setVar(root, '--custom-surface-variant-rgb', this.toRgb(palette.surfaceVariant) ?? palette.surfaceVariantRgb);
    this.setVar(root, '--custom-text', palette.text);
    this.setVar(root, '--custom-text-rgb', this.toRgb(palette.text) ?? palette.textRgb);
    this.setVar(root, '--custom-border', palette.border);
    this.setVar(root, '--custom-border-rgb', this.toRgb(palette.border) ?? palette.borderRgb);
  }

  private clearCustomThemeVars(): void {
    if (!this.isBrowser) {
      return;
    }

    const root = document.documentElement;
    const vars = [
      '--custom-primary',
      '--custom-primary-rgb',
      '--custom-secondary',
      '--custom-secondary-rgb',
      '--custom-control-accent',
      '--custom-control-accent-rgb',
      '--custom-info',
      '--custom-info-rgb',
      '--custom-success',
      '--custom-success-rgb',
      '--custom-warning',
      '--custom-warning-rgb',
      '--custom-danger',
      '--custom-danger-rgb',
      '--custom-gradient-primary',
      '--custom-gradient-accent',
      '--custom-gradient-warm',
      '--custom-gradient-cool',
      '--custom-sidebar-background',
      '--custom-header-background',
      '--custom-sidebar-color',
      '--custom-header-color',
      '--custom-sidebar-caption-color',
      '--custom-sidebar-hover-background',
      '--custom-sidebar-hover-color',
      '--custom-sidebar-active-background',
      '--custom-sidebar-active-color',
      '--custom-header-hover-background',
      '--custom-header-hover-color',
      '--custom-body-hover-background',
      '--custom-body-hover-color',
      '--custom-background',
      '--custom-background-rgb',
      '--custom-surface',
      '--custom-surface-rgb',
      '--custom-surface-variant',
      '--custom-surface-variant-rgb',
      '--custom-text',
      '--custom-text-rgb',
      '--custom-border',
      '--custom-border-rgb',
    ];

    vars.forEach((name) => root.style.removeProperty(name));
  }

  private setVar(root: HTMLElement, name: string, value?: string): void {
    if (value && value.trim().length > 0) {
      root.style.setProperty(name, value);
    } else {
      root.style.removeProperty(name);
    }
  }

  private toRgb(color?: string): string | undefined {
    if (!color) {
      return undefined;
    }

    const hex = color.trim();
    if (!hex.startsWith('#')) {
      return undefined;
    }

    const normalized = hex.slice(1);
    if (normalized.length !== 3 && normalized.length !== 6) {
      return undefined;
    }

    const full = normalized.length === 3
      ? normalized.split('').map((c) => c + c).join('')
      : normalized;

    const r = Number.parseInt(full.slice(0, 2), 16);
    const g = Number.parseInt(full.slice(2, 4), 16);
    const b = Number.parseInt(full.slice(4, 6), 16);

    if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
      return undefined;
    }

    return `${r}, ${g}, ${b}`;
  }

  private persistTheme(): void {
    const storage = this.getStorage();
    if (!storage) {
      return;
    }

    const current = this.currentTheme();
    storage.setItem(ThemeService.storageKey, JSON.stringify(current));
    if (current.fontId) {
      storage.setItem(ThemeService.fontIdKey, current.fontId);
    }
    if (current.fontFamily) {
      storage.setItem(ThemeService.fontFamilyKey, current.fontFamily);
    }
    if (current.fontSize) {
      storage.setItem(ThemeService.fontSizeKey, current.fontSize);
    }
  }

  private loadTheme(): ThemeState | null {
    const storage = this.getStorage();
    if (!storage) {
      return null;
    }

    const raw = storage.getItem(ThemeService.storageKey);
    if (!raw) {
      return null;
    }

    try {
      const parsed = this.normalizeTheme(JSON.parse(raw));
      if (!parsed.fontId) {
        parsed.fontId = storage.getItem(ThemeService.fontIdKey) ?? undefined;
      }
      if (!parsed.fontFamily) {
        parsed.fontFamily = storage.getItem(ThemeService.fontFamilyKey) ?? undefined;
      }
      if (!parsed.fontSize) {
        parsed.fontSize = storage.getItem(ThemeService.fontSizeKey) ?? undefined;
      }
      return parsed;
    } catch {
      return null;
    }
  }

  private normalizeTheme(theme: Partial<ThemeState> | null | undefined): ThemeState {
    const mode: ThemeMode = theme?.mode === 'dark' ? 'dark' : 'light';
    const name: ThemeName = theme?.name === 'custom' ? 'custom' : mode;
    const customByMode = name === 'custom' ? theme?.customByMode : undefined;
    let custom = name === 'custom' ? theme?.custom : undefined;
    if (name === 'custom' && customByMode) {
      custom = mode === 'dark'
        ? customByMode.dark ?? custom
        : customByMode.light ?? custom;
    }

    return {
      name,
      mode,
      custom: name === 'custom' ? custom : undefined,
      customByMode,
      fontId: theme?.fontId,
      fontFamily: theme?.fontFamily,
      fontSize: theme?.fontSize,
    };
  }

  private getStorage(): Storage | null {
    if (!this.isBrowser) {
      return null;
    }

    return window.localStorage;
  }
}
